/**
 * Overview card: home/away league rank with P, goals for:against, GD, points.
 */

import React, { useMemo } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { SectionCard } from '../SectionCard';
import { SoccerKit } from '../../ui/SoccerKit';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubMockData, MatchHubTablePositionSnapshot } from '../../../src/match/types/matchHub.types';
import { WEB_HOVER_TRANSITION } from '../../../src/shared/constants/animations';
import { getMatchHubCurrentTablePositionsCardStyles } from './MatchHubCurrentTablePositionsCard.styles';

export interface MatchHubCurrentTablePositionsCardProps {
  data: MatchHubMockData;
  onSeeAllPress?: () => void;
}

function formatGoals(s: MatchHubTablePositionSnapshot): string {
  return `${s.goalsFor}:${s.goalsAgainst}`;
}

function formatGd(gd: number): string {
  if (gd > 0) return `+${gd}`;
  return `${gd}`;
}

function TableRow({
  name,
  kit,
  snapshot,
  styles,
  isLast,
}: {
  name: string;
  kit: MatchHubMockData['home']['kit'];
  snapshot: MatchHubTablePositionSnapshot;
  styles: ReturnType<typeof getMatchHubCurrentTablePositionsCardStyles>;
  isLast: boolean;
}) {
  return (
    <View style={[styles.row, isLast && styles.rowLast]}>
      <View style={styles.left}>
        <Text style={styles.rank}>{snapshot.rank}</Text>
        <SoccerKit
          primary={kit.primary}
          secondary={kit.secondary}
          type={kit.type}
          size={28}
        />
        <Text style={styles.teamName} numberOfLines={1}>
          {name}
        </Text>
      </View>
      <View style={styles.headerLabels}>
        <Text style={[styles.statCell, styles.colP]}>{snapshot.played}</Text>
        <Text style={[styles.statCell, styles.colGoals]}>{formatGoals(snapshot)}</Text>
        <Text style={[styles.statCell, styles.colGd]}>{formatGd(snapshot.goalDifference)}</Text>
        <Text style={[styles.statCell, styles.colPts]}>{snapshot.points}</Text>
      </View>
    </View>
  );
}

export const MatchHubCurrentTablePositionsCard: React.FC<MatchHubCurrentTablePositionsCardProps> = ({
  data,
  onSeeAllPress,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubCurrentTablePositionsCardStyles(theme), [theme]);

  return (
    <SectionCard testID="match-hub-table-positions">
      <View style={styles.tableHeaderRow}>
        <View style={styles.tableHeaderSpacer} />
        <View style={styles.headerLabels}>
          <Text style={[styles.headerLabel, styles.colP]}>P</Text>
          <Text style={[styles.headerLabel, styles.colGoals]}>Goal</Text>
          <Text style={[styles.headerLabel, styles.colGd]}>+/-</Text>
          <Text style={[styles.headerLabel, styles.colPts]}>PTS</Text>
        </View>
      </View>
      <TableRow
        name={data.home.shortName}
        kit={data.home.kit}
        snapshot={data.tablePositions.home}
        styles={styles}
        isLast={false}
      />
      <TableRow
        name={data.away.shortName}
        kit={data.away.kit}
        snapshot={data.tablePositions.away}
        styles={styles}
        isLast
      />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="See full league table"
        onPress={onSeeAllPress ?? (() => undefined)}
        style={({ pressed, hovered }) => [
          styles.seeAllWrap,
          Platform.select({ web: { transition: WEB_HOVER_TRANSITION } as object }),
          pressed && styles.seeAllPressed,
          hovered && Platform.OS === 'web' && styles.seeAllHovered,
        ]}
      >
        <Text style={styles.seeAll}>See all</Text>
      </Pressable>
    </SectionCard>
  );
};
